'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { ComponentProps, ReactNode, createContext, useContext } from 'react';
import { QueryApi } from '@/features/query/api';
import { PAGINATION_LIMIT } from '@/shared/config/constants/api';
import { EmptyContainer } from '@/shared/ui/empty-container';
import { Icon } from '@/shared/ui/icon';
import { Layout } from '@/shared/ui/layout';
import { Modal } from '@/shared/ui/modal';
import { ModalProps } from '@/shared/ui/modal/types';
import { MyCouponsModalCouponItem } from './my-coupons-modal-coupon';
import './my-coupons-modal.scss';

type Props = ModalProps & {
    children?: ReactNode;
};

type MyCouponsModalContextValue = {
    couponsQuery: ReturnType<
        typeof useQuery<
            Awaited<ReturnType<NonNullable<ReturnType<typeof QueryApi.Customer.queries.Coupons>['queryFn']>>>
        >
    >;
};

const MyCouponsModalContext = createContext<MyCouponsModalContextValue | null>(null);

const useMyCouponsModalContext = () => {
    const context = useContext(MyCouponsModalContext);
    if (!context) {
        throw new Error('MyCouponsModal components must be used within MyCouponsModalWrapper.');
    }
    return context;
};

/** Provider that only provides context and renders children (Modal prefab). */
export const MyCouponsModalProvider = ({ children }: Props) => {
    // TODO: Infinite scroll
    const couponsQuery = useQuery(QueryApi.Customer.queries.Coupons({ offset: 0, limit: PAGINATION_LIMIT }));

    return (
        <MyCouponsModalContext.Provider value={{ couponsQuery }}>{children}</MyCouponsModalContext.Provider>
    );
};

export const MyCouponsModalWrapper = ({ children, ...props }: Props) => {
    return (
        <MyCouponsModalProvider {...props}>
            <Modal
                {...props}
                wrapperProps={{
                    className: 'my-coupons-modal',
                    containerProps: {
                        className: 'my-coupons-modal__container',
                    },
                    'data-testid': 'my-coupons-modal',
                }}
            >
                {children}
            </Modal>
        </MyCouponsModalProvider>
    );
};

/** Renders only the title text. Use inside prefab: "Modal.Title". */
const MyCouponsModalTitle = () => {
    const t = useTranslations();
    return <>{t('MY_COUPONS_MODAL.TITLE')}</>;
};

type ContentProps = ComponentProps<typeof Layout>;
const MyCouponsModalContent = ({ children, media, ...props }: ContentProps) => {
    const t = useTranslations();
    const { couponsQuery } = useMyCouponsModalContext();

    return (
        <Layout element="div" media={media} {...props}>
            {couponsQuery.data?.total === 0 && (
                <EmptyContainer
                    icon={(svgProps) => <Icon name="coupon" {...svgProps} />}
                    text={t('MY_COUPONS_MODAL.EMPTY_MESSAGE')}
                />
            )}
            {couponsQuery.data?.items?.map(({ coupon }) => (
                <MyCouponsModalCouponItem key={coupon.code} coupon={coupon} />
            ))}
            {children}
        </Layout>
    );
};

export { MyCouponsModalWrapper as MyCouponsModal };

export {
    MyCouponsModalWrapper as Wrapper,
    MyCouponsModalProvider as Provider,
    MyCouponsModalTitle as Title,
    MyCouponsModalContent as Content,
    MyCouponsModalCouponItem as CouponItem,
};
